/**
 * Seed output mapping section for API sequence view.
 *
 * Shows declared seed outputs with mapping fields to connect them
 * to API sequence step exports. Read-only display of declarations
 * with editable stepId + responsePath mappings.
 *
 * @module FlowDesigner/api/SeedOutputMappingSection
 */

import React from 'react';
import { CheckCircle, Warning, Info } from '@phosphor-icons/react';
import { PillToggle, Select, Text, TagBadge } from '@chatsdet/ui';
import type { SeedDataOutput } from '../../../../hooks/useSeedWorkflowOutputs';
import type { ApiSequenceExport, ApiSequenceStep, ApiSequenceStepResult } from '@chatsdet/types';
import { useApiSequenceSave, useApiSequenceRuns } from './hooks';
import { useResponsePathTypeahead } from './hooks/useResponsePathTypeahead';
import { ResponsePathTypeahead } from './ResponsePathTypeahead';
import { useApiRequestDetail } from '../../../../features/api-catalog/api';
import { useQueryClient } from '@tanstack/react-query';

type SeedOutputMappingSectionProps = {
  seedOutputs: SeedDataOutput[];
  exports: ApiSequenceExport[];
  steps: ApiSequenceStep[];
  sequenceId: string;
  workflowId: string;
};

/** Build a human-readable label for a step, preferring description */
function stepLabel(step: ApiSequenceStep, index: number): string {
  const method = (step.method ?? 'GET').toUpperCase();
  if (step.description) {
    return `${index + 1}. ${step.description}`;
  }
  const url = step.urlTemplate ?? '';
  let path = url;
  try {
    path = new URL(url).pathname;
  } catch {
    // Not a full URL — use as-is
  }
  return `${index + 1}. ${method} ${path}`;
}

/** Get a nested value from an object using dot notation. */
function getNestedValue(obj: unknown, path: string): unknown {
  const parts = path.split('.');
  let current: unknown = obj;

  for (const part of parts) {
    if (current === null || current === undefined) {
      return undefined;
    }
    if (typeof current === 'object') {
      current = (current as Record<string, unknown>)[part];
    } else {
      return undefined;
    }
  }

  return current;
}

/** Format a resolved value for display, truncating if necessary. */
function formatPreview(value: unknown): string {
  if (value === undefined) return '';
  if (value === null) return 'null';
  if (typeof value === 'string') {
    return value.length > 60 ? `"${value.slice(0, 57)}..."` : `"${value}"`;
  }
  if (typeof value === 'object') {
    const json = JSON.stringify(value);
    return json.length > 60 ? `${json.slice(0, 57)}...` : json;
  }
  return String(value);
}

/** Try to resolve a dot-notation path from a JSON string. */
function tryResolvePath(
  jsonStr: string | null | undefined,
  path: string
): unknown | undefined {
  if (!jsonStr) return undefined;
  try {
    return getNestedValue(JSON.parse(jsonStr), path);
  } catch {
    return undefined;
  }
}

type PathValidationResult =
  | { status: 'resolved'; value: unknown; source: 'last run' | 'catalog sample' }
  | { status: 'not-found'; source: 'last run' | 'catalog sample' }
  | { status: 'no-data' }
  | { status: 'none' };

type SeedOutputCardProps = {
  output: SeedDataOutput;
  matchingExport: ApiSequenceExport | undefined;
  isMapped: boolean;
  stepItems: { id: string; label: string }[];
  steps: ApiSequenceStep[];
  stepResults: ApiSequenceStepResult[] | undefined;
  onMappingChange: (dataKey: string, field: 'stepId' | 'responsePath' | 'source', value: string) => void;
};

const DEBOUNCE_MS = 400;

const SeedOutputCard: React.FC<SeedOutputCardProps> = ({
  output,
  matchingExport,
  isMapped,
  stepItems,
  steps,
  stepResults,
  onMappingChange,
}) => {
  const stepId = matchingExport?.stepId;
  const serverPath = matchingExport?.responsePath ?? '';
  const serverSource = matchingExport?.source ?? 'response';

  // Local state for debounced response path editing
  const [localPath, setLocalPath] = React.useState(serverPath);
  const [source, setSource] = React.useState<'request' | 'response'>(serverSource);
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync local state when server value changes (e.g. after save completes)
  React.useEffect(() => {
    setLocalPath(serverPath);
  }, [serverPath]);

  React.useEffect(() => {
    setSource(serverSource);
  }, [serverSource]);

  // Cleanup debounce on unmount
  React.useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handlePathChange = React.useCallback(
    (value: string) => {
      setLocalPath(value);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        onMappingChange(output.dataKey, 'responsePath', value);
      }, DEBOUNCE_MS);
    },
    [onMappingChange, output.dataKey]
  );

  const handlePathSelect = React.useCallback(
    (path: string) => {
      setLocalPath(path);
      // Save immediately on typeahead selection (no debounce)
      if (debounceRef.current) clearTimeout(debounceRef.current);
      onMappingChange(output.dataKey, 'responsePath', path);
    },
    [onMappingChange, output.dataKey]
  );

  // Use localPath for validation so it updates as you type
  const responsePath = localPath;

  // Find the step to get its apiRequestId for catalog fallback
  const step = stepId ? steps.find((s) => s.stepId === stepId) : undefined;
  const apiRequestId = step?.apiRequestId ?? null;

  // Only fetch catalog detail if we have a step selected but no run data for it
  const stepResult = stepId
    ? stepResults?.find((r) => r.stepId === stepId)
    : undefined;
  const needsCatalogFallback = source === 'response' && !!stepId && !stepResult?.body;

  const catalogQuery = useApiRequestDetail(
    needsCatalogFallback ? apiRequestId : null
  );

  // Pick the best JSON source for typeahead based on source toggle
  const jsonSource = source === 'request'
    ? (stepResult?.requestBody ?? null)
    : (stepResult?.body ?? catalogQuery.data?.data?.responseSample ?? null);

  const typeahead = useResponsePathTypeahead(jsonSource, localPath, handlePathSelect);

  // Compute validation result
  const validation = React.useMemo((): PathValidationResult => {
    if (!stepId || !responsePath) return { status: 'none' };

    // Try run data (request or response body based on source toggle)
    const runBody = source === 'request' ? stepResult?.requestBody : stepResult?.body;
    const runValue = tryResolvePath(runBody, responsePath);
    if (runValue !== undefined) return { status: 'resolved', value: runValue, source: 'last run' };
    if (runBody) return { status: 'not-found', source: 'last run' };

    // Catalog fallback (response source only — catalogs don't store request samples)
    if (source === 'response') {
      const catalogSample = catalogQuery.data?.data?.responseSample;
      const catalogValue = tryResolvePath(catalogSample, responsePath);
      if (catalogValue !== undefined) return { status: 'resolved', value: catalogValue, source: 'catalog sample' };
      if (catalogSample) return { status: 'not-found', source: 'catalog sample' };
    }

    return { status: 'no-data' };
  }, [stepId, responsePath, source, stepResult?.body, stepResult?.requestBody, catalogQuery.data?.data?.responseSample]);

  return (
    <div className="border rounded-lg bg-card overflow-hidden min-w-0">
      {/* Header row */}
      <div className="flex items-center gap-2 px-3 py-2 bg-muted/30 border-b border-border">
        {isMapped ? (
          <CheckCircle size={14} weight="fill" className="text-emerald-500 flex-shrink-0" />
        ) : (
          <Warning size={14} weight="fill" className="text-amber-500 flex-shrink-0" />
        )}
        <code className="text-xs font-mono font-semibold text-primary">{output.dataKey}</code>
        {output.name && output.name !== output.dataKey && (
          <Text size="xs" className="text-muted-foreground truncate">{output.name}</Text>
        )}
      </div>

      {/* Mapping fields */}
      <div className="px-3 py-2 space-y-1.5">
        {output.description && (
          <Text size="xs" className="text-muted-foreground">{output.description}</Text>
        )}

        <div className="grid grid-cols-2 gap-x-3 items-end">
          {/* Row 1: labels */}
          <label className="text-sm font-medium leading-none mb-1.5">Step</label>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-sm font-medium leading-none">
              {source === 'request' ? 'Request Path' : 'Response Path'}
            </label>
            <PillToggle
              value={source}
              onChange={(val) => {
                setSource(val);
                onMappingChange(output.dataKey, 'source', val);
              }}
              options={[
                { value: 'response' as const, label: 'Response' },
                { value: 'request' as const, label: 'Request' },
              ]}
            />
          </div>

          {/* Row 2: inputs (aligned) */}
          <Select
            placeholder="Select step..."
            items={stepItems}
            selectedKey={matchingExport?.stepId || null}
            onSelectionChange={(key) =>
              onMappingChange(output.dataKey, 'stepId', String(key))
            }
            searchable={false}
          />
          <div className="relative">
            <input
              data-control
              ref={typeahead.inputRef}
              type="text"
              className="flex h-9 w-full rounded-md border border-border bg-card px-3 py-2 text-sm font-mono shadow-sm placeholder:text-muted-foreground focus:outline-none focus-visible:border-ring/70 hover:border-ring/50 transition-colors"
              placeholder="e.g. data.id"
              value={localPath}
              onChange={(e) => handlePathChange(e.target.value)}
              onKeyDown={typeahead.onKeyDown}
              onFocus={typeahead.onFocus}
              onBlur={typeahead.onBlur}
            />
            {typeahead.isOpen && (
              <ResponsePathTypeahead
                suggestions={typeahead.suggestions}
                highlightedIndex={typeahead.highlightedIndex}
                onSelect={typeahead.selectSuggestion}
              />
            )}
          </div>
        </div>

        {/* Path validation feedback */}
        {validation.status === 'resolved' && (
          <div className="flex items-center gap-1.5 text-xs">
            <CheckCircle size={12} weight="fill" className="text-emerald-500 flex-shrink-0" />
            <span className="text-emerald-600 dark:text-emerald-400 font-mono truncate">
              → {formatPreview(validation.value)}
            </span>
            <span className="text-muted-foreground flex-shrink-0">(from {validation.source})</span>
          </div>
        )}
        {validation.status === 'not-found' && (
          <div className="flex items-center gap-1.5 text-xs">
            <Warning size={12} weight="fill" className="text-amber-500 flex-shrink-0" />
            <span className="text-amber-600 dark:text-amber-400">
              Path not found in {validation.source}
            </span>
          </div>
        )}
        {validation.status === 'no-data' && (
          <div className="flex items-center gap-1.5 text-xs">
            <Info size={12} weight="fill" className="text-muted-foreground flex-shrink-0" />
            <span className="text-muted-foreground">
              Run the sequence to validate this path
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export const SeedOutputMappingSection: React.FC<SeedOutputMappingSectionProps> = ({
  seedOutputs,
  exports: currentExports,
  steps,
  sequenceId,
  workflowId,
}) => {
  const saveMutation = useApiSequenceSave();
  const queryClient = useQueryClient();

  // Fetch latest run for step result data
  const runsQuery = useApiSequenceRuns(sequenceId, { page: 1, pageSize: 1 });
  const latestRun = runsQuery.data?.data?.[0];
  const stepResults = latestRun?.result?.stepResults;

  // Build a lookup from variableName -> export entry
  const exportMap = React.useMemo(() => {
    const map = new Map<string, ApiSequenceExport>();
    for (const exp of currentExports) {
      map.set(exp.variableName, exp);
    }
    return map;
  }, [currentExports]);

  const handleMappingChange = (
    dataKey: string,
    field: 'stepId' | 'responsePath' | 'source',
    value: string
  ) => {
    const existing = exportMap.get(dataKey);
    const updatedExport: ApiSequenceExport = {
      variableName: dataKey,
      stepId: existing?.stepId ?? '',
      responsePath: existing?.responsePath ?? '',
      source: existing?.source ?? 'response',
      description: existing?.description,
      [field]: value,
    };

    // Replace or add the export entry
    const newExports = currentExports.filter((e) => e.variableName !== dataKey);
    newExports.push(updatedExport);

    // Use the queryClient to get the cached sequence data for credentialId
    const cachedData = queryClient.getQueryData<{
      id: string;
      workflowId: string;
      credentialId: string | null;
      config: {
        steps: ApiSequenceStep[];
        exports?: ApiSequenceExport[];
        description?: string;
        stopOnFailure?: boolean;
      };
    }>(['api-sequence', workflowId]);
    if (!cachedData) return;

    saveMutation.mutate({
      workflowId,
      credentialId: cachedData.credentialId,
      config: {
        ...cachedData.config,
        exports: newExports,
      },
    });
  };

  // Filter out outputs with empty dataKeys
  const validOutputs = seedOutputs.filter((o) => o.dataKey);
  const hasFilteredOutputs = validOutputs.length < seedOutputs.length;

  // Build Select items with descriptive labels
  const stepItems = React.useMemo(
    () =>
      steps.map((s, i) => ({
        id: s.stepId ?? `step${i + 1}`,
        label: stepLabel(s, i),
      })),
    [steps]
  );

  return (
    <div className="mt-4 space-y-3">
      {/* Section header */}
      <div className="flex items-center gap-2">
        <Text size="sm" weight="semibold">Seed Data Outputs</Text>
        <TagBadge name={validOutputs.length} color="gray" hideDot size="sm" />
      </div>

      {/* Info banner */}
      <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-blue-500/5 border border-blue-500/20">
        <Info size={14} className="text-blue-500 flex-shrink-0 mt-0.5" weight="fill" />
        <Text size="xs" className="text-muted-foreground">
          Outputs are declared in Seed Data settings. Map each output to a step request or response below.
        </Text>
      </div>

      {/* Warning for filtered outputs */}
      {hasFilteredOutputs && (
        <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-amber-500/5 border border-amber-500/20">
          <Warning size={14} className="text-amber-500 flex-shrink-0 mt-0.5" weight="fill" />
          <Text size="xs" className="text-muted-foreground">
            Some seed outputs have missing data keys and are hidden. Configure them in the seed script settings.
          </Text>
        </div>
      )}

      {/* Output mapping cards */}
      {validOutputs.map((output) => {
        const matchingExport = exportMap.get(output.dataKey);
        const isMapped = !!(matchingExport?.stepId && matchingExport?.responsePath);

        return (
          <SeedOutputCard
            key={output.dataKey}
            output={output}
            matchingExport={matchingExport}
            isMapped={isMapped}
            stepItems={stepItems}
            steps={steps}
            stepResults={stepResults}
            onMappingChange={handleMappingChange}
          />
        );
      })}
    </div>
  );
};
